import React, { Fragment, useState } from 'react';
import { Alert, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const normalizeVTConfig = (config = {}) => ({
  ...config,
  id: Number(getSalesId(config) || 0),
  name: String(getSalesValue(config, 'typeName', 'TypeName', 'code', 'Code') || `Configuration ${getSalesId(config) || ''}`),
  detail: {
    typeName: getSalesValue(config, 'typeName', 'TypeName'),
    code: getSalesValue(config, 'code', 'Code'),
    baseDimensionLen: getSalesValue(config, 'baseDimensionLen', 'BaseDimensionLen'),
    baseDimensionWht: getSalesValue(config, 'baseDimensionWht', 'BaseDimensionWht'),
  },
});

const normalizeVTConfigs = (rows) => rows.map(normalizeVTConfig);

const VTConfigurationList = () => {
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [editState, setEditState] = useState(null);
  const [actionError, setActionError] = useState('');

  const {
    items: configs,
    setItems: setConfigs,
    loading,
    error,
    request,
    endpoints,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.verticalTurbineConfigurations.base, {
    normalize: normalizeVTConfigs,
    errorMessage: 'Unable to load vertical turbine configurations.',
  });

  const toggleExpand = (id) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async (configId, field) => {
    const currentConfig = configs.find((config) => config.id === configId);
    if (!currentConfig) return;

    const nextValue = field === 'baseDimensionLen' || field === 'baseDimensionWht'
      ? parseFloat(editState.value) || null
      : editState.value.trim() || null;

    const apiFieldMap = {
      typeName: 'TypeName',
      code: 'Code',
      baseDimensionLen: 'BaseDimensionLen',
      baseDimensionWht: 'BaseDimensionWht',
    };

    const payload = {
      ...currentConfig,
      Id: currentConfig.id,
      TypeName: currentConfig.detail.typeName,
      Code: currentConfig.detail.code,
      BaseDimensionLen: currentConfig.detail.baseDimensionLen,
      BaseDimensionWht: currentConfig.detail.baseDimensionWht,
      [apiFieldMap[field]]: nextValue,
    };

    setActionError('');
    try {
      await request({ method: 'PUT', url: endpoints.verticalTurbineConfigurations.byId(configId), data: payload });
      setConfigs((current) => current.map((config) => (config.id === configId ? normalizeVTConfig(payload) : config)));
      setEditState(null);
    } catch {
      setActionError('Unable to save vertical turbine configuration.');
    }
  };

  const renderInlineEdit = (configId, field, value, placeholder, inputType = 'text') => {
    const isEditing = editState?.configId === configId && editState?.field === field;
    if (isEditing) {
      return (
        <div className="d-flex align-items-center gap-2">
          <Form.Control type={inputType} size="sm" value={editState.value} onChange={(event) => setEditState((current) => ({ ...current, value: event.target.value }))} style={{ maxWidth: '160px' }} autoFocus />
          <button type="button" className="edit-icon" title="Save" onClick={() => handleSave(configId, field)}>
            <i className="bi bi-save"></i>
          </button>
        </div>
      );
    }
    return (
      <span>
        {value != null && value !== '' ? String(value) : <em style={{ color: '#999' }}>{placeholder}</em>}
        <button type="button" className="edit-icon" title="Edit" disabled={!canEditSales} onClick={() => setEditState({ configId, field, value: value != null ? String(value) : '' })}>
          <i className="bi bi-pencil-square"></i>
        </button>
      </span>
    );
  };

  return (
    <section className="sales-portal-page" aria-labelledby="sales-vt-config-title">
      <Row className="mb-3">
        <Col><h4 id="sales-vt-config-title">Vertical Turbine Configurations</h4></Col>
      </Row>

      {(error || actionError) && <Alert variant="danger">{error || actionError}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr className="table-border">
                  <th style={{ width: '3%' }}></th>
                  <th style={{ width: '80%' }}>Name</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={2}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading VT configurations...</div></td></tr>
                ) : configs.length === 0 ? (
                  <tr><td colSpan={2}><div className="sales-no-data">No Data Available</div></td></tr>
                ) : configs.map((config) => {
                  const isExpanded = expandedIds.has(config.id);
                  return (
                    <Fragment key={config.id}>
                      <tr className="table-border">
                        <td>
                          <button type="button" className="edit-icon" onClick={() => toggleExpand(config.id)} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${config.name}`}>
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                          </button>
                        </td>
                        <td title={config.name}>{config.name}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={2} style={{ padding: '0 0 0 30px', background: '#f8f9fa' }}>
                            <Table size="sm" className="mb-0" style={{ width: '80%' }}>
                              <thead>
                                <tr className="table-border">
                                  <th>Description</th>
                                  <th>Code</th>
                                  <th>Base Length</th>
                                  <th>Base Width</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="table-border">
                                  <td>{renderInlineEdit(config.id, 'typeName', config.detail.typeName, 'Please enter description')}</td>
                                  <td>{renderInlineEdit(config.id, 'code', config.detail.code, 'Please enter code')}</td>
                                  <td>{renderInlineEdit(config.id, 'baseDimensionLen', config.detail.baseDimensionLen, 'Please enter length', 'number')}</td>
                                  <td>{renderInlineEdit(config.id, 'baseDimensionWht', config.detail.baseDimensionWht, 'Please enter width', 'number')}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VTConfigurationList;
